// evaluate — the pure evaluation algebra: material × policy × boundary
// → per-constraint outcome (pass / warn / block / pending) + an
// aggregate report-card score.
//
// This is the load-bearing pure core. A total fold over Observations
// (`./observation.ts`) — no material, no I/O, no clocks, no casts. The
// shell produces the Observations and calls this to combine them with a
// Policy at a Boundary.
//
// The two dials compose exactly as `docs/model/constraints.md`
// §Severity versus Enforcement prescribes: a violation BLOCKS only when
// a `hard` membership meets a `gate` boundary; everywhere else it warns;
// a satisfied predicate passes. The report-card score is separate from
// (and never feeds) the block/warn decision — grade the value, enforce
// discretely.
//
// Pure module.
export { evaluate };
import { exhaustSwitchCase, Maybe } from "@bilda/utils";
import { enforcementFor } from "./boundary.js";
import * as ScoreMod from "./score.js";
// === evaluate ===
const evaluate = (policy, boundary, observations) => {
    const enforcement = enforcementFor(boundary);
    const byId = indexObservations(observations);
    const results = policy.members.map((member) => {
        const obs = byId.get(member.constraint.id);
        return resolveResult(member.severity, member.constraint, obs, enforcement);
    });
    return {
        policyId: policy.id,
        policyVersion: policy.version,
        boundary,
        enforcement,
        results,
        score: aggregateScore(results),
        blocks: results.some((r) => r.bite === "block"),
    };
};
// === Internals ===
const indexObservations = (observations) => {
    const m = new Map();
    for (const o of observations) {
        m.set(o.constraintId, o);
    }
    return m;
};
// One member's result. A missing observation is treated as pending: a
// judged constraint that hasn't been attested, or (defensively) a
// computed constraint the shell failed to supply — either way the core
// records "no verdict" rather than inventing one.
const resolveResult = (severity, constraint, obs, enforcement) => {
    if (obs === undefined || obs.type === "constraint_pending") {
        return {
            constraintId: constraint.id,
            production: constraint.production,
            severity,
            bite: "pending",
            score: Maybe.nothing,
        };
    }
    return {
        constraintId: constraint.id,
        production: constraint.production,
        severity,
        bite: biteFor(obs.outcome, severity, enforcement),
        score: Maybe.just(obs.score),
    };
};
// The 2×2 (× satisfied) enforcement table, as a total function.
const biteFor = (outcome, severity, enforcement) => {
    switch (outcome) {
        case "satisfied":
            return "pass";
        case "violated":
            return severity === "hard" && enforcement === "gate"
                ? "block"
                : "warn";
        default:
            return exhaustSwitchCase(outcome);
    }
};
// Report card: the mean of the measured scores, rounded to two decimals
// for a stable projection. `Nothing` when nothing was measured — an
// empty mean is undefined, not zero.
const aggregateScore = (results) => {
    const present = [];
    for (const r of results) {
        if (Maybe.isSome(r.score))
            present.push(ScoreMod.from(r.score.value));
    }
    if (present.length === 0)
        return Maybe.nothing;
    const mean = present.reduce((a, b) => a + b, 0) / present.length;
    const rounded = Math.round(mean * 100) / 100;
    return Maybe.just(ScoreMod.mk(rounded));
};
//# sourceMappingURL=evaluate.js.map